import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import { Loader } from '@ferris/react/components/loader';
import { TrackingContext } from '@vrbo/react-event-tracking';
import withPageFlow from '../../PageFlow/PageFlow';
import AppActions from '../../../actions/app';
import { getTrustPayload } from '../../../utils/TrustUtil';
import { createOutcomeEvent, createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';

export const UpdateAccount = (props) => {
    const {
        updateAccount,
        setUpdateAccountInProgress,
        locale,
        updateAccountInProgress,
        fingerprint,
        referrer,
        autoAuthUrl,
        pageFlow,
        loggedInUserType,
        publicuuid
    } = props;

    const { trigger } = useContext(TrackingContext);

    useEffect(() => {
        const initialTrustPayload = async () => {
            const trustPayload = await getTrustPayload();
            if (!updateAccountInProgress) {
                updateAccount(locale.canonical, fingerprint, trustPayload);
            }
        };

        trigger('pageLoad', { pageName: 'partner-qualifier: UpdateAccount', referrer });
        trigger('pageview', createPageViewEvent('Updateaccount'));
        // set a flag in redux to avoid multiple calls to updateAccount on rerender
        setUpdateAccountInProgress(true);
        initialTrustPayload();

        return () => {
            trigger('pageUnload', { pageName: 'partner-qualifier: UpdateAccount', referrer });
        };
    }, []);

    useEffect(() => {
        if (autoAuthUrl && updateAccountInProgress) {
            setUpdateAccountInProgress(false);
            if (loggedInUserType === 'traveler') {
                trigger('userAccountCreated', { publicuuid });
                trigger('outcome', createOutcomeEvent('user_account_creation.succeeded', 'UpdateAccount', 'UpgradeAccount', publicuuid));
            } else {
                trigger('outcome', createOutcomeEvent('user_account_upgraded.succeeded', 'UpdateAccount', 'UpgradeAccount', publicuuid));
            }
            pageFlow.next();
        }
    }, [autoAuthUrl, updateAccountInProgress]);

    return (
        <div className="update-account-wrapper">
            <Loader visible={updateAccountInProgress} />
        </div>
    );
};

UpdateAccount.propTypes = {
    updateAccountInProgress: PropTypes.bool.isRequired,
    setUpdateAccountInProgress: PropTypes.func.isRequired,
    updateAccount: PropTypes.func.isRequired,
    referrer: PropTypes.string.isRequired,
    loggedInUserType: PropTypes.string.isRequired,
    publicuuid: PropTypes.string.isRequired,
    locale: PropTypes.shape({
        canonical: PropTypes.string,
        display: PropTypes.string
    }).isRequired,
    autoAuthUrl: PropTypes.string,
    pageFlow: PropTypes.shape({
        next: PropTypes.func
    }).isRequired,
    fingerprint: PropTypes.shape({
        orgId: PropTypes.string,
        deviceId: PropTypes.string
    }).isRequired
};

UpdateAccount.defaultProps = {
    autoAuthUrl: undefined
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    updateAccount: (locale, fingerprint, trustPayload) => dispatch(
        AppActions.updateAccount(locale, fingerprint, trustPayload)
    ),
    setUpdateAccountInProgress: updateAccountInProgress => dispatch(
        AppActions.setUpdateAccountInProgress(updateAccountInProgress)
    )
});

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { app } = state;
    const {
        updateAccountInProgress,
        locale,
        updateAccountData,
        fingerprint,
        referrer,
        loggedInUserType
    } = app;

    return {
        updateAccountInProgress,
        locale,
        loggedInUserType,
        fingerprint,
        autoAuthUrl: updateAccountData ? updateAccountData.autoAuthUrl : '',
        publicuuid: updateAccountData ? updateAccountData.userPublicUuid : '',
        referrer
    };
};

const ConnectedUpdateAccount = connect(mapStateToProps, mapDispatchToProps)(UpdateAccount);
ConnectedUpdateAccount.displayName = 'UpdateAccount';
export default hot(module)(withPageFlow(ConnectedUpdateAccount));
